'use client';

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import styles from './direction-content.module.css';

type Layer = { key: string; content: ReactNode; textFallback: boolean; imagesFailed?: boolean };
type Layers = { current?: Layer; incoming?: Layer; fading: boolean; failedKey?: string };

export default function WallFrame({ contentKey, ready, textFallback, children }: {
  contentKey: string; ready: boolean; textFallback: boolean; children: ReactNode;
}) {
  const request = useRef({ key: contentKey, content: children, textFallback, ready });
  useLayoutEffect(() => {
    request.current = { key: contentKey, content: children, textFallback, ready };
  }, [contentKey, children, textFallback, ready]);
  const incomingRef = useRef<HTMLDivElement>(null);
  const [layers, setLayers] = useState<Layers>(() => ({
    incoming: ready ? request.current : undefined, fading: false,
  }));
  const finish = useCallback((expected?: Layer) => {
    setLayers(previous => previous.fading && previous.incoming && (!expected || previous.incoming === expected)
      ? { current: previous.incoming, fading: false } : previous);
  }, []);

  useEffect(() => {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const settle = () => { if (motion.matches || document.hidden) finish(); };
    const retry = () => setLayers(previous => previous.failedKey ? { ...previous, failedKey: undefined } : previous);
    motion.addEventListener('change', settle);
    document.addEventListener('visibilitychange', settle);
    window.addEventListener('online', retry);
    return () => {
      motion.removeEventListener('change', settle);
      document.removeEventListener('visibilitychange', settle);
      window.removeEventListener('online', retry);
    };
  }, [finish]);

  useEffect(() => {
    setLayers(previous => {
      // Начатое проявление заканчиваем; следующей будет только последняя выбранная цель.
      if (previous.fading) return previous;
      if (!ready || previous.current?.key === contentKey) {
        return previous.incoming ? { current: previous.current, fading: false } : previous;
      }
      if (previous.incoming?.key === contentKey || previous.failedKey === contentKey) return previous;
      return { current: previous.current, incoming: request.current, fading: false };
    });
  }, [contentKey, ready, layers.fading, layers.failedKey]);

  useEffect(() => {
    const incoming = layers.incoming;
    if (!incoming || layers.fading || !incomingRef.current) return;
    let cancelled = false;
    const images = Array.from(incomingRef.current.querySelectorAll('img'));
    // Декодируем именно будущие DOM-изображения, включая светлые копии под маской.
    const decoding = images.map(image => {
      image.loading = 'eager';
      return image.decode();
    });
    void Promise.allSettled(decoding).then(results => {
      if (cancelled || !request.current.ready || request.current.key !== incoming.key) return;
      const imagesFailed = results.some(result => result.status === 'rejected');
      const instant = document.hidden || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      setLayers(previous => {
        if (previous.incoming !== incoming || previous.fading) return previous;
        if (imagesFailed && !incoming.textFallback) {
          return { current: previous.current, failedKey: incoming.key, fading: false };
        }
        const prepared = imagesFailed ? { ...incoming, imagesFailed: true } : incoming;
        return instant ? { current: prepared, fading: false }
          : { current: previous.current, incoming: prepared, fading: true };
      });
    });
    return () => { cancelled = true; };
  }, [layers.incoming, layers.fading]);

  return <>
    {[layers.current, layers.incoming].map((layer, index) => {
      if (!layer) return null;
      const incoming = index === 1;
      const phase = incoming ? layers.fading ? 'entering' : 'preparing' : layers.fading ? 'leaving' : 'visible';
      const inactive = phase === 'preparing' || phase === 'leaving' || !ready || layer.key !== contentKey;
      return <div key={layer.key} ref={incoming ? incomingRef : undefined} className={styles.frameLayer}
        data-frame-layer={phase} data-frame-images-failed={layer.imagesFailed || undefined}
        aria-hidden={inactive || undefined} inert={inactive || undefined}
        onAnimationEnd={incoming ? event => { if (event.target === event.currentTarget) finish(layer); } : undefined}>
        {layer.content}
      </div>;
    })}
  </>;
}
